// src/pages/builder/TopicLibraryPanel.js
//
// The topic library: one record per topic, shared by every state that uses
// the slug. Set the video, song, image, game and bonus round here and every
// state carrying that slug picks it up.
//
// Sits above "Your library" in LayoutBuilder. Reads and writes `topicLibrary`
// and `mediaLibrary` only — it never touches portalLayouts, so nothing here
// can change a page that is already saved.
//
// WHY A TABLE RATHER THAN A LIST
// ------------------------------
// The reason to open this panel is almost always to scan for something wrong:
// which topics have no video, which have a song field holding an .mp4, which
// are still drafts. A list of titles hides exactly the columns you came to
// look at, so every field is a column and the media cells show the actual
// filename. Click a row to open the editor beneath it.
//
// ROW COLOUR AND STATUS FILTER
// ----------------------------
// The whole row is tinted by status — blue for live, warm orange for draft —
// rather than only the pill in the last column, which is the column furthest
// from where the eye starts and the first one a narrow window hides. "Needs
// attention" no longer tints the row, because it would have overwritten the
// status colour on the rows most worth looking at; it draws an orange bar
// down the left edge instead, and the offending cells are already red.
//
// The All / Live / Draft control beside the subject tabs narrows the table to
// one status within the current subject, and its selected state is painted in
// those same two colours. Counts are per subject and ignore the search box, so
// they read as a fixed fact about the subject rather than moving on every
// keystroke — the same way the subject tab counts already behave.
//
// STYLING
// -------
// Uses the builder's own .lb-btn classes and --lb-* custom properties instead
// of a private palette, so these buttons are the same blue as the rest of the
// page rather than a near-miss, and stay in step if that palette changes.
// Panel rules are namespaced .tl- AND scoped under `.lb-page .tl-panel`, so
// they cannot leak into any other screen — the same discipline as
// game-shell.css.
//
// WHY SOME MEDIA CELLS ARE RED
// ----------------------------
// seed-topic-library refuses to fill a field whose URL contradicts it — a
// musicUrl holding an .mp4 — and attaches it as a flagged option instead of
// dropping it, so the fix starts from what is actually there. Two topics have
// exactly that problem live right now, `nightandday` and `surfacechange`, and
// showing filenames instead of a tick is what makes them findable.
//
// LOOK-ALIKE TOPICS
// -----------------
// Pages resolve a library record by exact slug, so two records for the same
// song ("nightandday" / "night-and-day") are fully independent and a page
// only ever reads one of them. The drawer now opens with a notice saying how
// many state pages the open record reaches, whether it is a draft, and which
// look-alike records exist — with a button to edit that one instead or copy
// the lyrics across. The save toast reports the same thing instead of always
// claiming "every state now uses this".

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { exactKey as exactSlugKey } from "../../utils/topicKey";


import {
  SUBJECTS,
  SUBJECT_LABELS,
  emptyLibraryTopic,
  getAllLibraryTopics,
  saveLibraryTopic,
  setLibraryTopicStatus,
  toSlug,
  fileKeyOf,
  makeToolKey,
} from "../../services/topicLibraryService";
import {
  suggestFileName,
  uploadPortalMediaDetailed,
} from "../../services/mediaUploadService";
// The search-over-mediaLibrary picker already exists and is already used
// beside the videoUrl/musicUrl inputs below. Reusing it means one search
// implementation rather than two that drift apart.
import MediaPicker from "../../components/builder/MediaPicker";
// Read-only, one document. topicUsage/_all is written by
// scripts/audit-topic-usage.mjs and kept current by the Suggested Topics
// panel; computing it here would mean reading all 1,148 layout documents
// before the table could draw a single row.
import { doc, getDoc } from "firebase/firestore";
import { searchStandards } from "../../services/standardsService";
// Reused rather than reimplemented: the Suggested Topics panel already writes
// a topic onto every state page for a standard, with each state's own code,
// backing up as it goes. Attaching from here is the same operation reached
// from the other end.
import { markComplete } from "../../services/contentGapsService";
import { db, functions } from "../../firebase/firebaseConfig";
import { httpsCallable } from "firebase/functions";
import { AddToStateDialog, DeleteTopicDialog } from "./TopicLibraryActions";
import { pushDescription } from "../../services/topicPagesService";
import {
  BONUS_TYPE_VALUES,
  GAME_TYPE_VALUES,
} from "../../config/gameRegistry";

// A row is ONE library record, identified by its document id. Rows used to be
// identified by slug, and two records carrying the same slug
// ("continentsoceans" twice) collided: React mixed the two rows up, clicking
// one opened both, and edits could land on the row you weren't looking at.
const rowId = (t) => String(t?.id || t?.slug || "");

// These two lists used to be written out here by hand, which meant a game
// could be built, wired into UniversalQuizGame and playable — and still be
// impossible to select on a topic, because nobody remembered this file. They
// are now derived from the one catalogue in src/config/gameRegistry.js, so a
// game added there appears in this dropdown and in the builder's Game library
// panel at the same time, with no second edit.
//
// The registry holds no component imports, so reading these costs this panel
// nothing — none of the game code or its stylesheets comes with them.
const GAME_TYPES = GAME_TYPE_VALUES;
const BONUS_TYPES = BONUS_TYPE_VALUES;

// singAlongUrl is the MP4 behind the portal's 🎤 Sing-along button (empty =
// no button). It is a video, so it uses the video picker, but uploads get
// their own name — <slug><year>singalong.mp4, the same name the Sing-along
// studio saves — so an upload here can never collide with, or offer to
// overwrite, the topic's regular <slug><year>video.mp4.
//
// fileName receives the topic's FILE KEY (fileKeyOf(makeToolKey(...))), not
// its raw slug: earth'swater and earthswater are two topics and must never
// be handed the same filename.
const MEDIA_FIELDS = [
  { field: "videoUrl", type: "video", label: "Video" },
  { field: "musicUrl", type: "music", label: "Song" },
  {
    field: "singAlongUrl",
    type: "video",
    label: "Sing-along video",
    fileName: (fileKey, file) =>
      `${fileKey}${new Date().getFullYear()}singalong${(String(file?.name || "").match(/\.[^.]+$/) || [".mp4"])[0].toLowerCase()}`,
  },
  { field: "imageUrl", type: "image", label: "Image" },
];

// What the file dialog offers for each kind of media. Matches the extensions
// mediaUploadService accepts, so the dialog can't offer a file the upload
// would then refuse.
const ACCEPT = {
  video: "video/*,.mp4,.mov,.webm,.m4v",
  music: "audio/*,.mp3,.wav,.m4a,.ogg",
  image: "image/*,.png,.jpg,.jpeg,.webp,.gif,.svg",
};

const AUDIO_RE = /\.(mp3|wav|m4a|ogg)(\?|$)/i;
const VIDEO_RE = /\.(mp4|mov|webm|m4v)(\?|$)/i;

/** The filename out of a Firebase download URL, for display in a table cell. */
const fileNameOf = (url) => {
  const match = String(url || "").match(/\/o\/([^?]+)/);
  if (!match) return "";
  try {
    return decodeURIComponent(match[1]).split("/").pop();
  } catch {
    return match[1].split("/").pop();
  }
};

/** Does this URL contradict the field holding it? */
const mediaMismatch = (field, url) => {
  if (!url) return "";
  const name = fileNameOf(url) || url;
  if (field === "musicUrl" && VIDEO_RE.test(name)) return "video file in the song field";
  if (field === "videoUrl" && AUDIO_RE.test(name)) return "audio file in the video field";
  if (field === "singAlongUrl" && AUDIO_RE.test(name)) return "audio file in the sing-along field";
  return "";
};

/* ------------------------------------------------------------------ */
/* Lyrics                                                              */
/* ------------------------------------------------------------------ */
//
// A blank row between two verses is content, not an artefact, so it survives
// the save. What does NOT survive is a blank row at the very top or the very
// bottom, which is what you get from pressing Enter one more time than you
// meant to — those render as a gap above the first line or below the last with
// nothing in the UI to explain them.
//
// The popup is what turns a stored blank row into a visible break: see
// PortalMediaPopup.js, which renders an empty entry as a spacer element. An
// empty <p> would not have worked, because its margins collapse against the
// paragraphs either side and the gap stays exactly the same size as a normal
// line break. Typing <br> into this box does not work either — the popup
// renders each entry as text, so it would appear on screen as the literal
// characters "<br>".

/** Whatever is stored, as an array of strings. */
const asLines = (value) => {
  if (Array.isArray(value)) return value.map((line) => String(line ?? ""));
  if (typeof value === "string") return value.split("\n");
  return [];
};

/** Drop blank rows at the start and end; keep every blank row in between. */
const trimBlankEdges = (lines) => {
  const out = lines.map((line) => line.replace(/\s+$/, ""));
  while (out.length && !out[0].trim()) out.shift();
  while (out.length && !out[out.length - 1].trim()) out.pop();
  return out;
};

/* ------------------------------------------------------------------ */
/* Look-alike topics                                                   */
/* ------------------------------------------------------------------ */
//
// WHY THIS EXISTS
// ---------------
// A state page picks up a library record by EXACT slug. "nightandday" and
// "night-and-day" are two separate documents, and a page carries only one
// of them. Editing the record no page points at saves perfectly well — the
// write succeeds, the row updates — and changes nothing a student sees. That
// looked exactly like "the save didn't work".
//
// Two topics are look-alikes when their slugs or titles match once case,
// spaces, hyphens and underscores are ignored. The drawer uses this to say,
// before you type a word, which of the pair the pages are actually reading.

/** Letters and digits only, lowercased — the comparison key for look-alikes. */
const looseKey = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");

/* ------------------------------------------------------------------ */
/* Media control (editor drawer)                                       */
/* ------------------------------------------------------------------ */

const MediaField = ({ field, type, label, fileName, fileKey, topic, onChange, onToast }) => {
  const [busy, setBusy] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  // Why the preview can't play, in words. A <video> that can't decode its
  // file just shows a grey box and a dead play button — this says so instead.
  const [playError, setPlayError] = useState("");
  const fileInput = useRef(null);

  const value = topic[field] || "";
  useEffect(() => setPlayError(""), [value]);

  const onMediaError = (event) => {
    const e = event.currentTarget?.error;
    const why = {
      2: "a network error stopped it loading",
      3: "the browser couldn't decode it",
      4: "the browser doesn't support this file's format, or the file is damaged",
    }[e?.code] || "the browser couldn't play it";
    setPlayError(`${why}${e?.message ? ` (${e.message})` : ""}`);
  };
  const options = topic.mediaOptions?.[field] || [];
  const needsPick = !value && options.length > 0;
  const mismatch = mediaMismatch(field, value);

  // Images are not in mediaLibrary — indexStorageMedia only walks videos/,
  // music/ and music-math/. Offering a search that can only ever return
  // nothing reads as a broken picker, so images go straight to upload.
  const searchable = type !== "image";

  const doUpload = async (file, overwrite = false) => {
    setBusy(true);
    try {
      const result = await uploadPortalMediaDetailed({
        file,
        topicSlug: topic.slug,
        fileKey,
        mediaType: type,
        fileName: fileName ? fileName(fileKey, file) : undefined,
        overwrite,
      });
      onChange(field, result.url);
      setPendingFile(null);
      onToast(`Uploaded ${result.name} to ${result.path}.`);
    } catch (error) {
      if (error.code === "sbl/file-exists") {
        // Not an error to parse — it is a decision, so it becomes a confirm
        // step rather than a red message.
        setPendingFile({ file, path: error.path });
      } else {
        onToast(error.message, "bad");
      }
    } finally {
      setBusy(false);
    }
  };

  const onFile = (event) => {
    const file = event.target.files?.[0];
    if (file) doUpload(file);
    event.target.value = "";
  };

  return (
    <div className={`tl-media ${needsPick || mismatch ? "is-flagged" : ""}`}>
      <div className="tl-media-head">
        <span className="tl-media-label">{label}</span>
        {mismatch ? (
          <span className="tl-pill tl-pill-bad">{mismatch}</span>
        ) : value ? (
          <span className="tl-pill tl-pill-ok">set</span>
        ) : needsPick ? (
          <span className="tl-pill tl-pill-warn">{options.length} candidates</span>
        ) : (
          <span className="tl-pill">empty</span>
        )}
      </div>

      {value && (
        <div className="tl-media-preview">
          {type === "image" ? (
            <img src={value} alt="" onError={() => setPlayError("the picture didn't load")} />
          ) : type === "music" ? (
            <audio key={value} controls preload="metadata" src={value} onError={onMediaError} />
          ) : (
            <video key={value} controls playsInline preload="metadata" src={value} onError={onMediaError} />
          )}
          <code className="tl-file">{fileNameOf(value) || value}</code>
          {playError && (
            <p className="tl-play-error">
              <strong>Won’t play:</strong> {playError}.
              {field === "singAlongUrl" || /animated\.|singalong\./i.test(fileNameOf(value))
                ? " If this was made in the Sing-along or Lesson video studio before the exporter fix, make it again there and save — that replaces this file."
                : " Try opening it in a new tab; if it won’t play there either, upload a new copy."}{" "}
              <a href={value} target="_blank" rel="noreferrer">Open in a new tab ↗</a>
            </p>
          )}
          <button
            type="button"
            className="lb-btn lb-btn-sm lb-btn-ghost"
            onClick={() => onChange(field, "")}
          >
            Clear
          </button>
        </div>
      )}

      {/* Candidates the seeder found but refused to choose between. The state
          count is usually the fastest way to tell which file won in practice
          without listening to both. */}
      {needsPick && (
        <ul className="tl-options">
          {options.map((o) => (
            <li key={o.path || o.url} className={o.warning ? "is-bad" : ""}>
              <button type="button" onClick={() => onChange(field, o.url)}>
                {o.file}
              </button>
              <span>{o.warning ? o.warning : `${o.copies} states`}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="tl-media-actions">
        {searchable && (
          <MediaPicker
            type={type}
            topicSlug={topic.slug}
            triggerClassName="lb-btn lb-btn-sm lb-btn-soft"
            triggerLabel={`Find existing ${type}`}
            onSelect={(url) => {
              onChange(field, url);
              onToast(`${label} set.`);
            }}
          />
        )}

        {pendingFile ? (
          <div className="tl-confirm">
            <p>
              <strong>{pendingFile.path}</strong> already exists. Replacing it
              changes this media for every topic pointing at that file, not
              just this one.
            </p>
            <div className="tl-confirm-actions">
              <button
                type="button"
                className="lb-btn lb-btn-sm lb-btn-danger"
                onClick={() => doUpload(pendingFile.file, true)}
              >
                Replace it
              </button>
              <button
                type="button"
                className="lb-btn lb-btn-sm lb-btn-ghost"
                onClick={() => setPendingFile(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          // A real <label> around the file input, rather than a hidden input
          // opened with fileInput.click(). The click() version depends on
          // the browser treating a script-triggered click as the user's own;
          // when it doesn't, the button does nothing and says nothing — which
          // is what this button was doing. Clicking a label is the browser's
          // own way to open a file dialog and works everywhere.
          <label
            className={`lb-btn lb-btn-sm lb-btn-soft tl-upload ${
              busy || !topic.slug ? "is-disabled" : ""
            }`}
            aria-disabled={busy || !topic.slug}
          >
            {busy ? "Uploading…" : "Upload new"}
            <input
              ref={fileInput}
              type="file"
              className="tl-file-input"
              accept={ACCEPT[type] || undefined}
              disabled={busy || !topic.slug}
              onChange={onFile}
            />
          </label>
        )}
      </div>

      {!topic.slug && <p className="tl-tiny">Set a slug first.</p>}

      {topic.slug && !pendingFile && searchable && (
        <p className="tl-tiny">
          An upload is saved as{" "}
          <code>
            {/* Same name the upload will really use: a field with its own
                fileName (the sing-along video) must show that, not the
                regular <slug>2026video.mp4 it would never write. */}
            {fileName
              ? fileName(fileKey, { name: type === "music" ? ".mp3" : ".mp4" })
              : suggestFileName({
                  topicSlug: topic.slug,
                  fileKey,
                  mediaType: type,
                  file: { name: type === "music" ? ".mp3" : ".mp4" },
                })}
          </code>{" "}
          in the shared folder.
        </p>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Table cell                                                          */
/* ------------------------------------------------------------------ */

const MediaCell = ({ topic, field }) => {
  const value = topic[field] || "";
  const mismatch = mediaMismatch(field, value);
  const candidates = topic.mediaOptions?.[field]?.length || 0;

  if (mismatch) {
    return (
      <span className="tl-cell tl-cell-bad" title={`${mismatch} — ${value}`}>
        {fileNameOf(value)}
      </span>
    );
  }
  if (value) {
    return (
      <span className="tl-cell tl-cell-ok" title={value}>
        {fileNameOf(value) || "set"}
      </span>
    );
  }
  if (candidates) {
    return <span className="tl-cell tl-cell-warn">{candidates} to pick from</span>;
  }
  return <span className="tl-cell tl-cell-empty">—</span>;
};

/* ------------------------------------------------------------------ */
/* Panel                                                               */
/* ------------------------------------------------------------------ */

const TopicLibraryPanel = () => {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState("science");
  const [search, setSearch] = useState("");
  const [onlyProblems, setOnlyProblems] = useState(false);
  // "all" | "live" | "draft". Sits alongside the subject tabs rather than
  // replacing them: the question is almost always "what is still draft in
  // math", not "what is still draft anywhere".
  const [status, setStatus] = useState("all");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  // Collapsed by default, matching State availability and Suggested topics.
  // Three always-open panels made the builder a long page to scroll past on
  // the way to the layout itself.
  const [open, setOpen] = useState(false);
  // slug -> { stateCount, states[] }. Null until loaded, which is also what
  // shows when the usage audit has never been run.
  const [usage, setUsage] = useState(null);
  const [usageAt, setUsageAt] = useState(null);
  const [showStates, setShowStates] = useState(null);
  // ➕ Add to state / 🗑 Delete pop-ups (TopicLibraryActions.js)
  const [addTo, setAddTo] = useState(null);
  const [delTopic, setDelTopic] = useState(null);
  // ✨ descriptions written from the lyrics (functions/topicDescriptions.js)
  const [descBusy, setDescBusy] = useState(false);
  const [bulkDesc, setBulkDesc] = useState(null); // { total, done, made, failed, running }

  // Attach-to-standard, from inside the topic editor. Kept in one object
  // because these six pieces are only ever meaningful together.
  const [attach, setAttach] = useState({
    subject: "science",
    grade: "3rd",
    state: "",
    term: "",
    results: null,
    searching: false,
    chosen: null,
    picked: null,
    running: false,
    done: null,
    error: "",
  });
  const setA = (patch) => setAttach((a) => ({ ...a, ...patch }));

  const runSearch = async () => {
    setA({ searching: true, error: "", results: null, chosen: null });
    try {
      const r = await searchStandards(attach.subject, attach.grade, attach.term, {
        state: attach.state,
      });
      setA({ results: r, searching: false });
    } catch (err) {
      setA({ searching: false, error: err?.message || "Search failed." });
    }
  };

  const runAttach = async () => {
    const g = attach.chosen;
    if (!g || !draft?.slug) return;
    // Narrow to the chosen states. The service writes exactly what it is
    // given, so the scope decision belongs here.
    const scoped =
      attach.picked && attach.picked.size < g.states.length
        ? { ...g, states: g.states.filter((x) => attach.picked.has(x.state)) }
        : g;
    setA({ running: true, error: "", done: null });
    try {
      const r = await markComplete({ ...scoped, docId: `${g.subject}_${g.grade}` }, draft.slug);
      setA({ running: false, done: r, chosen: null, results: null });
      setUsage(null); // force the States column to recount
    } catch (err) {
      setA({ running: false, error: err?.message || "Attach failed." });
    }
  };

  // The editor drawer opens BELOW a table that can be 500px tall, so by the
  // time you have finished editing you are a long way down the page. Closing
  // the drawer then shortens the document under you and leaves the viewport
  // pointing at whatever happens to be beneath the panel — usually the next
  // card entirely. Returning to the panel heading is the only outcome that
  // matches what the click meant: this topic is done, show me the list.
  const panelRef = useRef(null);

  const scrollToPanelTop = useCallback(() => {
    // Two frames, not one: the first lets React unmount the drawer, the second
    // runs after layout has settled at the new, shorter document height. A
    // single frame measures the old position and lands short.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const node = panelRef.current;
        if (!node) return;
        const top = node.getBoundingClientRect().top + window.scrollY - 16;
        window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
      });
    });
  }, []);

  const showToast = useCallback((message, tone = "ok") => {
    setToast({ message, tone });
    window.setTimeout(() => setToast(null), 4500);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTopics(await getAllLibraryTopics());
    } catch (error) {
      showToast(`Could not load the library: ${error.message}`, "bad");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  // The Topic pictures panel writes imageUrl / imageDraft straight to the
  // library. Take those changes in here too, so saving a topic that was
  // loaded earlier can't put back the old (empty) picture.
  useEffect(() => {
    const onUpdated = (e) => {
      const { id, patch } = e.detail || {};
      if (!id || !patch) return;
      setTopics((prev) => prev.map((t) => ((t.id || t.slug) === id ? { ...t, ...patch } : t)));
      setDraft((prev) => (prev && (prev.id || prev.slug) === id ? { ...prev, ...patch } : prev));
    };
    window.addEventListener("sbl:topic-updated", onUpdated);
    return () => window.removeEventListener("sbl:topic-updated", onUpdated);
  }, []);

  /** A topic worth a second look: missing media, a mismatch, or an open pick. */
  const problemsOn = useCallback((t) => {
    const flags = [];
    MEDIA_FIELDS.forEach(({ field }) => {
      const bad = mediaMismatch(field, t[field]);
      if (bad) flags.push(bad);
      else if (!t[field] && t.mediaOptions?.[field]?.length) flags.push("needs a pick");
    });
    return flags;
  }, []);

  // Anything that is not exactly "published" is a draft, which is the same
  // test the Status column and the row tint use. Keeping the three in step
  // matters more than being clever about it — a row that reads "draft" and
  // survives the Live filter would make the filter untrustworthy.
  const isLive = useCallback((t) => t.status === "published", []);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return topics
      .filter((t) => (t.subject || "other") === subject)
      // records the Duplicate topics merge set aside only show under "Merged"
      .filter((t) => (status === "merged" ? t.status === "merged" : t.status !== "merged"))
      .filter(
        (t) => status === "all" || status === "merged" || (isLive(t) ? status === "live" : status === "draft")
      )
      .filter((t) => !needle || `${t.slug} ${t.title}`.toLowerCase().includes(needle))
      .filter((t) => !onlyProblems || problemsOn(t).length > 0);
  }, [topics, subject, status, search, onlyProblems, problemsOn, isLive]);

  const counts = useMemo(
    () =>
      SUBJECTS.reduce((acc, s) => {
        const all = topics.filter((t) => (t.subject || "other") === s);
        const rows = all.filter((t) => t.status !== "merged");
        const live = rows.filter(isLive).length;
        acc[s] = {
          total: rows.length,
          live,
          draft: rows.length - live,
          merged: all.length - rows.length,
          flagged: rows.filter((t) => problemsOn(t).length > 0).length,
        };
        return acc;
      }, {}),
    [topics, problemsOn, isLive]
  );

  const setField = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));

  // Counted separately so the label reports what a reader will see: how many
  // lines are actually sung, and how many deliberate gaps sit between them. A
  // single "12 lines" that silently included three blanks was the thing that
  // made the blanks look like they had not saved.
  const lyricsText = useMemo(() => asLines(draft?.lyrics).join("\n"), [draft]);

  const lyricsStats = useMemo(() => {
    const lines = asLines(draft?.lyrics);
    const sung = lines.filter((line) => line.trim().length > 0).length;
    return { total: lines.length, sung, breaks: lines.length - sung };
  }, [draft]);

  /** State pages carrying this exact slug; null while the count is loading. */
  const stateCountOf = useCallback(
    (slug) => (usage === null ? null : usage[slug]?.stateCount || 0),
    [usage]
  );

  // Which key this snapshot of the library gives each topic's tools. The old
  // stripped slug for every ordinary topic; a topic's own exact slug when
  // stripping would land on a DIFFERENT topic (earth'swater vs earthswater).
  // See src/utils/topicKey.js.
  const toolKey = useMemo(() => makeToolKey(topics), [topics]);

  // Other library records that look like the open one. Matched on slug OR
  // title, because "similar topics" are usually the same title saved twice
  // under two slugs.
  const lookAlikes = useMemo(() => {
    if (!draft?.slug) return [];
    const slugKey = looseKey(draft.slug);
    const titleKey = looseKey(draft.title);
    // Every OTHER record (by document id) — including one carrying the very
    // same slug, which the old slug comparison skipped.
    return topics.filter(
      (t) =>
        rowId(t) !== rowId(draft) &&
        ((slugKey && looseKey(t.slug) === slugKey) ||
          (titleKey && looseKey(t.title) === titleKey))
    );
  }, [draft, topics]);

  // What saving THIS record will actually reach. Worked out once so the
  // drawer notice, the save button hint and the toast all say the same thing.
  const reach = useMemo(() => {
    if (!draft?.slug) return null;
    const count = stateCountOf(draft.slug);
    return {
      count,
      live: draft.status === "published",
      // Look-alikes that ARE on pages — the ones a student is really seeing.
      onPages: lookAlikes.filter((t) => (stateCountOf(t.slug) || 0) > 0),
    };
  }, [draft, lookAlikes, stateCountOf]);

  const [copying, setCopying] = useState("");

  /** Put the lyrics currently in the editor onto another library record. */
  const copyLyricsTo = async (target) => {
    const lines = trimBlankEdges(asLines(draft?.lyrics));
    if (!lines.length) {
      showToast("There are no lyrics in the editor to copy.", "bad");
      return;
    }
    if (
      !window.confirm(
        `Replace the lyrics on "${target.slug}" with the ${lines.filter((l) => l.trim()).length} lines in this editor?\n\n` +
          `That record is on ${stateCountOf(target.slug) || 0} state page(s).`
      )
    ) {
      return;
    }
    setCopying(target.slug);
    try {
      const saved = await saveLibraryTopic({ ...target, lyrics: lines });
      setTopics((prev) => prev.map((t) => ((t.id || t.slug) === saved.id ? saved : t)));
      showToast(
        saved.status === "published"
          ? `Lyrics copied to "${saved.slug}".`
          : `Lyrics copied to "${saved.slug}", but it is a draft — publish it for pages to show them.`,
        saved.status === "published" ? "ok" : "bad"
      );
    } catch (error) {
      showToast(error.message, "bad");
    } finally {
      setCopying("");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const saved = await saveLibraryTopic({
        ...draft,
        // Blank rows BETWEEN verses are kept — that is how a stanza break is
        // authored. Blank rows at the top and bottom are dropped, because
        // those come from an extra Enter rather than from the song.
        lyrics: trimBlankEdges(asLines(draft.lyrics)),
      });
      setTopics((prev) => {
        // Matched on the document id. Matching on a stripped slug is what
        // made saving earth'swater replace the earthswater row.
        const rest = prev.filter((t) => (t.id || t.slug) !== saved.id);
        return [...rest, saved].sort((a, b) =>
          String(a.title || a.slug).localeCompare(String(b.title || b.slug))
        );
      });
      setDraft(null);
      scrollToPanelTop();

      // The old message promised "every state now uses this" even for a
      // record on zero pages, which is what made a successful save read as a
      // broken one. Say what actually happened.
      const count = stateCountOf(saved.slug);
      if (saved.status !== "published") {
        showToast(
          "Saved as a draft. Pages keep showing the old version until you publish it.",
          "bad"
        );
      } else if (count === 0) {
        showToast(
          `Saved — but no state page uses "${saved.slug}", so students will not see this change.`,
          "bad"
        );
      } else if (count) {
        showToast(`Saved. ${count} state${count === 1 ? "" : "s"} using "${saved.slug}" now show this.`);
      } else {
        showToast(`Saved. Every state page using "${saved.slug}" now shows this.`);
      }
    } catch (error) {
      showToast(error.message, "bad");
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Draft / Live toggle ---------- */
  //
  // The button in the drawer header. For a topic that already exists it saves
  // the STATUS ONLY, straight away — any other edits in the form stay unsaved
  // until "Save topic", so a quick publish can never push a half-typed lyric
  // to every state. A brand-new topic has no document yet, so for it the
  // button just sets what "Save topic" will write.
  const [statusBusy, setStatusBusy] = useState(false);

  const isSavedTopic = Boolean(draft && (draft.id || draft.createdAt || draft.sourceCopies));

  // The stored version of the open record, for "you also have unsaved edits".
  const storedDraft = useMemo(
    () => (draft ? topics.find((t) => (t.id || t.slug) === (draft.id || draft.slug)) || null : null),
    [draft, topics]
  );

  const hasOtherEdits = useMemo(() => {
    if (!draft || !storedDraft) return false;
    const norm = (v) => (v === undefined || v === null ? "" : JSON.stringify(v));
    const keys = new Set([...Object.keys(draft), ...Object.keys(storedDraft)]);
    keys.delete("status");
    keys.delete("updatedAt");
    for (const k of keys) {
      const a = k === "lyrics" ? trimBlankEdges(asLines(draft[k])) : draft[k];
      const b = k === "lyrics" ? trimBlankEdges(asLines(storedDraft[k])) : storedDraft[k];
      if (norm(a) !== norm(b)) return true;
    }
    return false;
  }, [draft, storedDraft]);

  const toggleStatus = async () => {
    if (!draft) return;
    const next = draft.status === "published" ? "draft" : "published";
    if (!isSavedTopic) {
      setField("status", next);
      return;
    }
    if (next === "draft") {
      const n = stateCountOf(draft.slug) || 0;
      if (
        n > 0 &&
        !window.confirm(
          `Move "${draft.title || draft.slug}" back to draft?\n\nThe ${n} state page${n === 1 ? "" : "s"} using it will stop showing the library version until you publish it again.`
        )
      ) {
        return;
      }
    }
    setStatusBusy(true);
    try {
      const id = draft.id || draft.slug;
      await setLibraryTopicStatus(id, next);
      setDraft((prev) => (prev ? { ...prev, status: next } : prev));
      setTopics((prev) => prev.map((t) => ((t.id || t.slug) === id ? { ...t, status: next } : t)));
      const extra = hasOtherEdits ? " Your other changes in this form are not saved yet — press Save topic." : "";
      showToast(
        next === "published"
          ? `Published "${draft.title || draft.slug}". State pages using it now show it.${extra}`
          : `"${draft.title || draft.slug}" is a draft again. Students no longer see the library version.${extra}`,
        next === "published" ? "ok" : "bad"
      );
    } catch (error) {
      showToast(`Could not change the status: ${error.message}`, "bad");
    } finally {
      setStatusBusy(false);
    }
  };

  // Delete opens the pop-up that also deals with the pages using the topic
  // (TopicLibraryActions.js). It deletes by document id, so deleting
  // earth'swater can never delete earthswater.
  const remove = (topic) => {
    if (topic?.id || topic?.slug) setDelTopic(topic);
  };
  const afterDelete = (id) => {
    setTopics((prev) => prev.filter((t) => (t.id || t.slug) !== id));
    setDraft((d) => (d && (d.id || d.slug) === id ? null : d));
    setUsage(null); // the States column recounts
  };

  /* ---------- ✨ descriptions from the lyrics ---------- */
  const describe = httpsCallable(functions, "describeTopic");
  const lyricLines = (t) => asLines(t?.lyrics).filter((l) => l.trim());

  const writeDescription = async () => {
    if (!draft) return;
    if (!lyricLines(draft).length) {
      showToast("Add the lyrics first — the description is written from them.", "bad");
      return;
    }
    setDescBusy(true);
    try {
      const { data } = await describe({ title: draft.title, subject: SUBJECT_LABELS[draft.subject] || draft.subject, lyrics: lyricLines(draft) });
      setField("description", data.description);
      showToast("Description written — check it, then Save topic.");
    } catch (e) {
      showToast(e?.message || "Couldn't write a description.", "bad");
    } finally {
      setDescBusy(false);
    }
  };

  const useDescriptionOnPages = async () => {
    const text = String(draft?.description || "").trim();
    if (!text || !draft?.slug) return;
    if (!window.confirm(`Put this description on every page that has its own wording for "${draft.slug}"?\n\nPages without their own already show the library's description once you save.`)) return;
    setDescBusy(true);
    try {
      const r = await pushDescription({ slug: draft.slug, description: text, overwrite: true });
      showToast(r.changed ? `Description put on ${r.changed} page${r.changed === 1 ? "" : "s"} that had their own (backed up first).` : "No page had its own wording — they all show the library's description.");
    } catch (e) {
      showToast(e?.message || "Couldn't update the pages.", "bad");
    } finally {
      setDescBusy(false);
    }
  };

  // every topic in this subject with lyrics and no description
  const missingDesc = useMemo(
    () => topics.filter((t) => (t.subject || "other") === subject && t.status !== "merged" && !String(t.description || "").trim() && lyricLines(t).length),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [topics, subject]
  );

  const writeAllDescriptions = async () => {
    const list = missingDesc;
    if (!list.length) return;
    if (!window.confirm(`Write a description for ${list.length} ${SUBJECT_LABELS[subject]} topic${list.length === 1 ? "" : "s"} from their lyrics?\n\nEach is saved to the library straight away, and shows on every page that has no wording of its own.`)) return;
    setBulkDesc({ total: list.length, done: 0, made: 0, failed: 0, running: true });
    for (const t of list) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await describe({ title: t.title, subject: SUBJECT_LABELS[t.subject] || t.subject, lyrics: lyricLines(t) });
        // eslint-disable-next-line no-await-in-loop
        const saved = await saveLibraryTopic({ ...t, description: data.description });
        setTopics((prev) => prev.map((x) => ((x.id || x.slug) === saved.id ? { ...x, description: data.description } : x)));
        setBulkDesc((b) => ({ ...b, done: b.done + 1, made: b.made + 1 }));
      } catch (e) {
        setBulkDesc((b) => ({ ...b, done: b.done + 1, failed: b.failed + 1, error: e?.message }));
        if (e?.code === "functions/failed-precondition" || e?.code === "functions/permission-denied") break;
      }
    }
    setBulkDesc((b) => ({ ...b, running: false }));
  };

  useEffect(() => {
    if (!open || usage !== null) return undefined;
    let alive = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "topicUsage", "_all"));
        if (!alive) return;
        setUsage(snap.exists() ? snap.data().usage || {} : {});
        setUsageAt(snap.exists() ? snap.data().generatedAt : null);
      } catch {
        if (alive) setUsage({});
      }
    })();
    return () => { alive = false; };
  }, [open, usage]);

  const totalFlagged = SUBJECTS.reduce((n, s) => n + (counts[s]?.flagged || 0), 0);

  return (
    <section className="lb-card tl-panel" ref={panelRef}>
      <div className="lb-card-head">
        <span className="lb-step tl-step">◆</span>
        <div>
          <h2>Topic library</h2>
          <p className="lb-hint">
            One record per topic, shared by every state using that slug. Set the
            media, game and bonus round once here. Standard numbers and
            per-state wording stay on each page.
          </p>
        </div>

        <div className="tl-head-actions">
          <button
            type="button"
            className="lb-btn lb-btn-soft"
            onClick={load}
            disabled={loading}
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
          <button
            type="button"
            className="lb-btn"
            onClick={() => setDraft({ ...emptyLibraryTopic(), subject })}
          >
            + New topic
          </button>
          <button type="button" className="lb-btn" onClick={() => setOpen((v) => !v)}>
            {open ? "Hide" : "Open"}
          </button>
        </div>
      </div>

      {open && (<>
      <div className="tl-controls">
        <div className="tl-tabs">
          {SUBJECTS.map((s) => (
            <button
              key={s}
              type="button"
              className={`tl-tab ${subject === s ? "is-on" : ""}`}
              onClick={() => setSubject(s)}
            >
              {SUBJECT_LABELS[s]}
              <span className="tl-tab-count">{counts[s]?.total ?? 0}</span>
              {counts[s]?.flagged > 0 && (
                <span className="tl-tab-flag">{counts[s].flagged}</span>
              )}
            </button>
          ))}
        </div>

        <div className="tl-seg" role="group" aria-label="Filter by status">
          {[
            { key: "all", label: "All", n: counts[subject]?.total ?? 0 },
            { key: "live", label: "Live", n: counts[subject]?.live ?? 0 },
            { key: "draft", label: "Draft", n: counts[subject]?.draft ?? 0 },
            ...(counts[subject]?.merged ? [{ key: "merged", label: "Merged", n: counts[subject].merged }] : []),
          ].map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={`tl-seg-btn tl-seg-${opt.key} ${
                status === opt.key ? "is-on" : ""
              }`}
              aria-pressed={status === opt.key}
              onClick={() => setStatus(opt.key)}
            >
              {opt.label}
              <span className="tl-seg-n">{opt.n}</span>
            </button>
          ))}
        </div>

        <input
          className="tl-filter"
          value={search}
          placeholder="Filter by slug or title…"
          onChange={(e) => setSearch(e.target.value)}
        />

        <button
          type="button"
          className={`lb-btn lb-btn-sm ${onlyProblems ? "lb-btn-danger" : "lb-btn-ghost"}`}
          onClick={() => setOnlyProblems((v) => !v)}
          title="Missing media, a file in the wrong field, or an unresolved pick"
        >
          {onlyProblems ? "Showing problems only" : `Needs attention (${totalFlagged})`}
        </button>
      </div>

      {(missingDesc.length > 0 || bulkDesc) && (
        <div className="tl-bulk-desc">
          {bulkDesc ? (
            <span>
              {bulkDesc.running ? "✨ Writing descriptions… " : "✨ Done: "}
              {bulkDesc.made} written{bulkDesc.failed ? `, ${bulkDesc.failed} failed${bulkDesc.error ? ` (${bulkDesc.error})` : ""}` : ""} · {bulkDesc.done}/{bulkDesc.total}
            </span>
          ) : (
            <span>
              {missingDesc.length} {SUBJECT_LABELS[subject]} topic{missingDesc.length === 1 ? " has" : "s have"} lyrics but no description.
            </span>
          )}
          {!bulkDesc?.running && missingDesc.length > 0 && (
            <button type="button" className="lb-btn lb-btn-sm" onClick={writeAllDescriptions}>
              ✨ Write them from the lyrics
            </button>
          )}
          {bulkDesc && !bulkDesc.running && (
            <button type="button" className="lb-btn lb-btn-sm lb-btn-ghost" onClick={() => setBulkDesc(null)}>
              Hide
            </button>
          )}
        </div>
      )}

      {loading ? (
        <p className="tl-tiny">Loading…</p>
      ) : !visible.length ? (
        <p className="tl-empty">
          {onlyProblems ? (
            "Nothing flagged in this subject."
          ) : status !== "all" ? (
            `No ${status} topics in ${SUBJECT_LABELS[subject]}${
              search.trim() ? " match that search" : ""
            }.`
          ) : counts[subject]?.total ? (
            "Nothing matches that search."
          ) : (
            <>
              Nothing in {SUBJECT_LABELS[subject]} yet. Seed it with{" "}
              <code>
                node src/scripts/seed-topic-library.mjs --subject={subject} --write
              </code>
            </>
          )}
        </p>
      ) : (
        <div className="tl-table-wrap">
          <table className="tl-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Slug</th>
                <th>Video</th>
                <th>Song</th>
                <th>Sing-along</th>
                <th>Image</th>
                <th>Lyrics</th>
                <th>Game</th>
                <th>Bonus</th>
                <th>States</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {visible.map((t, rowIndex) => {
                const flags = problemsOn(t);
                const open = Boolean(draft) && rowId(draft) === rowId(t);
                // never two rows with one key: React leaves the extra rows
                // stuck on screen after the next search when keys repeat
                const seenKey = rowId(t);
                const key = visible.findIndex((x) => rowId(x) === seenKey) === rowIndex ? seenKey : `${seenKey}#${rowIndex}`;
                return (
                  <tr
                    key={key}
                    className={[
                      t.status === "published" ? "is-live" : "is-draft",
                      flags.length ? "is-flagged" : "",
                      open ? "is-open" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => setDraft(open ? null : { ...t })}
                  >
                    <td className="tl-td-title">{t.title || t.slug}</td>
                    <td>
                      <code className="tl-slug">{t.slug}</code>
                    </td>
                    <td>
                      <MediaCell topic={t} field="videoUrl" />
                    </td>
                    <td>
                      <MediaCell topic={t} field="musicUrl" />
                    </td>
                    <td>
                      <MediaCell topic={t} field="singAlongUrl" />
                    </td>
                    <td>
                      <MediaCell topic={t} field="imageUrl" />
                    </td>
                    <td>
                      {t.lyrics?.length ? (
                        <span className="tl-cell tl-cell-ok">
                          {asLines(t.lyrics).filter((l) => l.trim()).length} lines
                        </span>
                      ) : (
                        <span className="tl-cell tl-cell-empty">—</span>
                      )}
                    </td>
                    <td>
                      <span className="tl-type">{t.gameType || "—"}</span>
                    </td>
                    <td>
                      <span className="tl-type">{t.bonusRoundType || "—"}</span>
                    </td>
                    <td>
                      {/* Zero means finished work on no page — the opposite
                          problem to a gap, and worth seeing. */}
                      {usage === null ? (
                        <span className="tl-cell tl-cell-empty">…</span>
                      ) : (
                        <button
                          type="button"
                          className={`tl-states ${
                            (usage[t.slug]?.stateCount || 0) ? "tl-states-on" : "tl-states-off"
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowStates({
                              slug: t.slug,
                              title: t.title || t.slug,
                              states: usage[t.slug]?.states || [],
                            });
                          }}
                          title="Which states use this topic"
                        >
                          {usage[t.slug]?.stateCount || 0}
                        </button>
                      )}
                    </td>
                    <td>
                      <span
                        className={`tl-pill ${
                          t.status === "published" ? "tl-pill-live" : "tl-pill-draft"
                        }`}
                      >
                        {t.status === "published" ? "live" : t.status === "merged" ? "merged" : "draft"}
                      </span>
                    </td>
                    <td>
                      <span className="tl-row-actions">
                        <button
                          type="button"
                          className="tl-row-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAddTo(t);
                          }}
                          title="Put this topic on a state's page"
                        >
                          ➕ Add to state
                        </button>
                        <button
                          type="button"
                          className="tl-row-btn is-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDelTopic(t);
                          }}
                          title="Delete this topic (and choose what replaces it on pages)"
                          aria-label={`Delete ${t.title || t.slug}`}
                        >
                          🗑
                        </button>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {draft && (
        <div className="tl-drawer">
          <div className="tl-drawer-head">
            <div className="tl-drawer-title">
              <h3>{draft.slug ? draft.title || draft.slug : "New topic"}</h3>
              {draft.slug && <code className="tl-slug">{draft.slug}</code>}
            </div>

            {/* DRAFT / LIVE — one click. Saves the status only (see
                toggleStatus); the pill says where the topic is now and the
                button says where it will go. */}
            <div className={`tl-status-toggle ${draft.status === "published" ? "is-live" : "is-draft"}`}>
              <span className="tl-status-now">
                <span className="tl-status-dot" aria-hidden="true" />
                {draft.status === "published" ? "Live" : "Draft"}
              </span>
              <button
                type="button"
                className={`lb-btn lb-btn-sm ${draft.status === "published" ? "lb-btn-ghost" : ""} tl-status-btn`}
                disabled={statusBusy}
                onClick={toggleStatus}
                title={
                  isSavedTopic
                    ? "Saves the status straight away. Other edits still need Save topic."
                    : "New topic: this is what Save topic will write."
                }
              >
                {statusBusy
                  ? "Saving…"
                  : draft.status === "published"
                  ? "Move to draft"
                  : "Publish"}
              </button>
            </div>

            <button
              type="button"
              className="lb-btn lb-btn-sm lb-btn-ghost"
              onClick={() => {
                setDraft(null);
                scrollToPanelTop();
              }}
            >
              Close
            </button>
          </div>
          {!isSavedTopic && (
            <p className="tl-tiny tl-status-note">
              New topic — it will save as{" "}
              <strong>{draft.status === "published" ? "Live" : "Draft"}</strong> when you press Save topic.
            </p>
          )}

          {/* WHO SEES THIS RECORD
              Shown before the fields, not beside the Save button, because the
              time to learn you are editing the wrong one of a pair is before
              you retype the song — not after. */}
          {reach && (
            <div
              className={`tl-reach ${
                !reach.live || reach.count === 0 ? "tl-reach-warn" : "tl-reach-ok"
              }`}
            >
              {!reach.live ? (
                <p>
                  <strong>This topic is a draft.</strong> State pages ignore it and keep
                  showing their old lyrics until you press <strong>Publish</strong> above.
                </p>
              ) : reach.count === 0 ? (
                <p>
                  <strong>No state page uses <code>{draft.slug}</code>.</strong> Saving
                  here will work, but no student will see the change.
                  {reach.onPages.length > 0 && " The pages are reading the record below."}
                </p>
              ) : reach.count ? (
                <p>
                  <strong>
                    On {reach.count} state page{reach.count === 1 ? "" : "s"}.
                  </strong>{" "}
                  Changes here reach every one of them once saved.
                </p>
              ) : (
                <p>Counting which state pages use this topic…</p>
              )}

              {lookAlikes.some((t) => exactSlugKey(t.slug) === exactSlugKey(draft.slug)) && (
                <p className="tl-same-slug">
                  <strong>⚠ Another record has the exact same slug, <code>{draft.slug}</code>.</strong>{" "}
                  Every page using that slug reads just one of them, and which one can change from day to
                  day — that is why its video, song or sing-along seem to swap. Use{" "}
                  <strong>🧩 Duplicate topics</strong> (just below the Topic library) to merge them into one.
                </p>
              )}

              {lookAlikes.length > 0 && (
                <>
                  <p className="tl-tiny">
                    Look-alike topic{lookAlikes.length === 1 ? "" : "s"} in the library.
                    Each is a separate record — a page only reads the one whose slug it
                    carries, so editing one never changes the other.
                  </p>
                  <ul className="tl-twins">
                    {lookAlikes.map((t) => {
                      const n = stateCountOf(t.slug);
                      return (
                        <li key={rowId(t)}>
                          <code>{t.slug}</code>
                          {exactSlugKey(t.slug) === exactSlugKey(draft.slug) && <span className="tl-pill tl-pill-draft">same slug</span>}
                          <span className={`tl-states ${n ? "tl-states-on" : "tl-states-off"}`}>
                            {n === null ? "…" : `${n} state${n === 1 ? "" : "s"}`}
                          </span>
                          <span
                            className={`tl-pill ${
                              t.status === "published" ? "tl-pill-live" : "tl-pill-draft"
                            }`}
                          >
                            {t.status === "published" ? "live" : "draft"}
                          </span>
                          <span className="tl-twins-actions">
                            <button
                              type="button"
                              className="lb-btn lb-btn-sm lb-btn-soft"
                              onClick={() => setDraft({ ...t })}
                            >
                              Edit this one instead
                            </button>
                            <button
                              type="button"
                              className="lb-btn lb-btn-sm lb-btn-ghost"
                              disabled={Boolean(copying) || !asLines(draft.lyrics).some((l) => l.trim())}
                              onClick={() => copyLyricsTo(t)}
                            >
                              {copying === t.slug ? "Copying…" : "Copy these lyrics to it"}
                            </button>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}

              {usageAt && (
                <p className="tl-tiny">
                  Page counts from {new Date(usageAt).toLocaleDateString()}. If you attached
                  this topic since then, run{" "}
                  <code>node src/scripts/audit-topic-usage.mjs --write</code> to recount.
                </p>
              )}
            </div>
          )}

          {/* ---- 1. Basics ---- */}
          <section className="tl-section tl-sec-basics">
            <header className="tl-section-head">
              <span className="tl-section-num">1</span>
              <div>
                <h4>Basics</h4>
                <p>Name, subject and the games this topic plays.</p>
              </div>
            </header>
          <div className="tl-grid">
            <label>
              <span>Title <b className="tl-req">required</b></span>
              <input
                value={draft.title}
                onChange={(e) => {
                  const title = e.target.value;
                  setDraft((prev) => ({
                    ...prev,
                    title,
                    // Auto-fill the slug on a NEW topic only. Changing it on an
                    // existing one would orphan every layout pointing at the
                    // old slug, and the write would create a new document
                    // rather than update this one.
                    slug: prev.id || prev.createdAt || prev.sourceCopies ? prev.slug : toSlug(title),
                  }));
                }}
              />
            </label>

            <label>
              <span>Slug</span>
              <input
                value={draft.slug}
                disabled={Boolean(draft.id || draft.createdAt || draft.sourceCopies)}
                onChange={(e) => setField("slug", toSlug(e.target.value))}
              />
              {(draft.id || draft.createdAt || draft.sourceCopies) && (
                <em className="tl-tiny">Locked — it is the document ID.</em>
              )}
            </label>

            <label>
              <span>Subject</span>
              <select
                value={draft.subject}
                onChange={(e) => setField("subject", e.target.value)}
              >
                {SUBJECTS.map((s) => (
                  <option key={s} value={s}>
                    {SUBJECT_LABELS[s]}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Game</span>
              <select
                value={draft.gameType}
                onChange={(e) => setField("gameType", e.target.value)}
              >
                {GAME_TYPES.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Bonus round</span>
              <select
                value={draft.bonusRoundType}
                onChange={(e) => setField("bonusRoundType", e.target.value)}
              >
                {BONUS_TYPES.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </label>
          </div>
          </section>

          {/* ---- 2. Description ---- */}
          <section className="tl-section tl-sec-desc">
            <header className="tl-section-head">
              <span className="tl-section-num">2</span>
              <div>
                <h4>Description</h4>
                <p>1–2 sentences. Every state page using this topic shows it, unless that page has its own wording.</p>
              </div>
            </header>
          <label className="tl-block">
            <span>Description</span>
            <textarea
              rows={3}
              value={draft.description}
              onChange={(e) => setField("description", e.target.value)}
            />
            <div className="tl-desc-tools">
              <button type="button" className="lb-btn lb-btn-sm" onClick={writeDescription} disabled={descBusy}>
                {descBusy ? "Writing…" : "✨ Write from the lyrics"}
              </button>
              {draft.id && String(draft.description || "").trim() && (
                <button type="button" className="lb-btn lb-btn-sm lb-btn-ghost" onClick={useDescriptionOnPages} disabled={descBusy}>
                  Also use on pages with their own wording
                </button>
              )}
            </div>
            <em className="tl-tiny">
              Saved with the topic. Pages with no description of their own show this one (and any state you add it to
              gets it). Standard numbers stay per page.
            </em>
          </label>
          </section>

          {/* LYRICS
              PortalMediaPopup renders topic.lyrics as one element per array
              entry, so the editor is a plain textarea split on newlines: one
              line of the song per row, exactly as it will appear under the
              player. What you type here is what renders — there is no markup
              layer, so <br> would show up on screen as those five characters.

              A blank row is how you get a gap between verses. It is stored as
              an empty string and the popup renders it as a spacer with real
              height, which is why an empty <p> was not enough: paragraph
              margins collapse against their neighbours, so the "gap" came out
              the same size as an ordinary line break. */}
          {/* ---- 3. Lyrics ---- */}
          <section className="tl-section tl-sec-lyrics">
            <header className="tl-section-head">
              <span className="tl-section-num">3</span>
              <div>
                <h4>Lyrics</h4>
                <p>One sung line per row. A blank row makes a gap between verses.</p>
              </div>
            </header>
          <label className="tl-block">
            <span>
              Lyrics
              {lyricsStats.total > 0 && (
                <em className="tl-count">
                  {" "}
                  · {lyricsStats.sung} lines
                  {lyricsStats.breaks > 0 &&
                    `, ${lyricsStats.breaks} break${
                      lyricsStats.breaks === 1 ? "" : "s"
                    }`}
                </em>
              )}
            </span>
            <textarea
              className="tl-lyrics"
              rows={Math.min(Math.max(lyricsStats.total + 2, 6), 20)}
              value={lyricsText}
              placeholder={
                "One line of the song per row.\n" +
                "Leave a row empty to put a gap between verses.\n" +
                "\n" +
                "Like this — the empty row above becomes a break."
              }
              onChange={(e) =>
                // Split only. No filtering, no trimming of the array: an empty
                // row has to survive being typed, or the cursor jumps a line
                // the moment you press Enter twice. The edges are tidied at
                // save time instead, where it cannot fight the keyboard.
                setField("lyrics", e.target.value.split("\n"))
              }
            />
            <em className="tl-tiny">
              Shown in the music popup beneath the player.{" "}
              <strong>Leave a row blank to skip a line</strong> — that blank row
              is kept and renders as a gap between verses. Blank rows at the very
              top or bottom are dropped. Do not type <code>&lt;br&gt;</code>; it
              would appear on screen as text.
            </em>
          </label>

          {/* Lyrics -> games. Opens the Game generator panel on this topic;
              it reads the lyrics from topicLibrary, so save first. */}
          {lyricsStats.sung > 0 && (
            <div className="tl-gen-games">
              <button
                type="button"
                className="lb-btn lb-btn-sm lb-btn-soft"
                onClick={() =>
                  window.dispatchEvent(
                    new CustomEvent("sbl:open-game-generator", {
                      detail: { slug: draft.slug, id: draft.id || draft.slug },
                    })
                  )
                }
              >
                🎲 Make games from these lyrics
              </button>
              <span className="tl-tiny"> Save first if you just changed the lyrics.</span>
            </div>
          )}
          </section>

          {/* ---- 4. Media ---- */}
          <section className="tl-section tl-sec-media">
            <header className="tl-section-head">
              <span className="tl-section-num">4</span>
              <div>
                <h4>Media</h4>
                <p>Video, song, sing-along video and picture. Each box is one file.</p>
              </div>
            </header>
          <div className="tl-media-row">
            {MEDIA_FIELDS.map((m) => (
              <MediaField
                key={m.field}
                {...m}
                fileKey={fileKeyOf(toolKey(draft.id || draft.slug || ""))}
                topic={draft}
                onChange={setField}
                onToast={showToast}
              />
            ))}
          </div>
          </section>

          {/* ---- Add this topic to a standard ---- */}
          {/* The Suggested Topics panel starts from a gap and asks for a slug.
              This starts from the topic and asks for a standard — the same
              attach, reached from whichever end you happen to be at. */}
          <section className="tl-section tl-sec-attach tl-attach">
            <header className="tl-section-head">
              <span className="tl-section-num">5</span>
              <div>
                <h4>Add to a standard</h4>
                <p>Put this topic on a state page for a standard it also teaches. Optional.</p>
              </div>
            </header>

            <div className="tl-attach-row">
              <select
                value={attach.subject}
                onChange={(e) => setA({ subject: e.target.value, results: null, chosen: null })}
                aria-label="Subject"
              >
                <option value="science">Science</option>
                <option value="math">Math</option>
                <option value="socialstudies">Social Studies</option>
              </select>
              <select
                value={attach.grade}
                onChange={(e) => setA({ grade: e.target.value, results: null, chosen: null })}
                aria-label="Grade"
              >
                {["k","1st","2nd","3rd","4th","5th","6th","7th","8th"].map((g) => (
                  <option key={g} value={g}>{g === "k" ? "Kindergarten" : `${g} grade`}</option>
                ))}
              </select>
              <input
                placeholder="Code or words from the standard…"
                value={attach.term}
                onChange={(e) => setA({ term: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runSearch(); } }}
                aria-label="Search standards"
              />
              <button type="button" className="lb-btn" onClick={runSearch} disabled={attach.searching}>
                {attach.searching ? "Searching…" : "Search"}
              </button>
            </div>

            {attach.error && <p className="tl-tiny tl-attach-error">{attach.error}</p>}

            {attach.done && (
              <p className="tl-tiny tl-attach-done">
                Attached to {attach.done.added} state page
                {attach.done.added === 1 ? "" : "s"}
                {attach.done.already ? `, ${attach.done.already} already had it` : ""}
                {attach.done.missing ? `, ${attach.done.missing} have no layout yet` : ""}.
              </p>
            )}

            {attach.results && !attach.chosen && (
              <ul className="tl-attach-results">
                {attach.results.map((r) => (
                  <li key={r.code}>
                    <button
                      type="button"
                      onClick={() => setA({
                        chosen: r,
                        picked: new Set(r.states.map((x) => x.state)),
                      })}
                    >
                      <span className={`tl-states ${r.stateCount > 1 ? "tl-states-on" : "tl-states-off"}`}>
                        {r.stateCount}
                      </span>{" "}
                      <code>{r.code}</code>
                      <span className="tl-attach-desc">{r.description}</span>
                    </button>
                  </li>
                ))}
                {!attach.results.length && (
                  <li className="tl-tiny">No standard matches that in {attach.grade} {attach.subject}.</li>
                )}
              </ul>
            )}

            {attach.chosen && (
              <div className="tl-attach-confirm">
                <p>
                  Add <strong>{draft.title || draft.slug}</strong> to{" "}
                  <code>{attach.chosen.code}</code>?
                </p>

                {/* Honest about which case this is. "All states alike" is only
                    meaningful for NGSS and Common Core codes, where the same
                    string really is published by many states. Arkansas's 3.E.1
                    and Georgia's SS3E1 are the same idea and nothing in the
                    data says so, and offering a cross-state attach there would
                    silently do nothing. */}
                {attach.chosen.shared ? (
                  <p className="tl-tiny">
                    This is a shared code, so {attach.chosen.stateCount} states publish
                    it. Each will show its own version of the number.
                  </p>
                ) : (
                  <p className="tl-tiny">
                    This is {attach.chosen.states[0]?.stateName}&apos;s own numbering. No
                    other state publishes this code, so there is nothing to match
                    it against elsewhere.
                  </p>
                )}

                <div className="tl-attach-picks">
                  {attach.chosen.states.map((x) => (
                    <label key={x.state}>
                      <input
                        type="checkbox"
                        checked={attach.picked?.has(x.state) || false}
                        onChange={(e) =>
                          setAttach((a) => {
                            const next = new Set(a.picked);
                            if (e.target.checked) next.add(x.state);
                            else next.delete(x.state);
                            return { ...a, picked: next };
                          })
                        }
                      />
                      {x.state.toUpperCase()} <code>{x.code}</code>
                    </label>
                  ))}
                </div>

                <div className="tl-attach-row">
                  <button
                    type="button"
                    className="lb-btn"
                    disabled={attach.running || !attach.picked?.size}
                    onClick={runAttach}
                  >
                    {attach.running
                      ? "Attaching…"
                      : `Attach to ${attach.picked?.size || 0} state${
                          attach.picked?.size === 1 ? "" : "s"
                        }`}
                  </button>
                  <button type="button" className="lb-btn" onClick={() => setA({ chosen: null })}>
                    Back
                  </button>
                </div>
              </div>
            )}
          </section>

          <div className="tl-drawer-actions">
            <button
              type="button"
              className="lb-btn"
              disabled={saving || !draft.slug || !draft.title}
              onClick={save}
            >
              {saving ? "Saving…" : "Save topic"}
            </button>
            {draft.createdAt && (
              <button
                type="button"
                className="lb-btn lb-btn-sm lb-btn-danger"
                onClick={() => remove(draft)}
              >
                Delete
              </button>
            )}
            {/* Counted from topicUsage, not from draft.sourceStates.
                sourceStates was written once by seed-topic-library.mjs when the
                library was created and has never updated — it said 22 for a
                topic now on 31 states, which is the kind of number people stop
                trusting after being wrong once. */}
            {draft.status === "published" && (usage?.[draft.slug]?.stateCount ?? 0) > 1 && (
              <span className="tl-tiny">
                Saving changes this topic for {usage[draft.slug].stateCount} states.
              </span>
            )}
          </div>
        </div>
      )}

      {/* Which states, on demand. A count alone raises the question straight
          away, and the answer is already in the document we loaded. */}
      {addTo && (
        <AddToStateDialog
          topic={addTo}
          onClose={() => setAddTo(null)}
          onDone={() => {
            setUsage(null);
            showToast(`“${addTo.title || addTo.slug}” added.`);
          }}
        />
      )}
      {delTopic && (
        <DeleteTopicDialog
          topic={delTopic}
          topics={topics}
          onClose={() => {
            setDelTopic(null);
            scrollToPanelTop();
          }}
          onDeleted={afterDelete}
        />
      )}

      {showStates && (
        <div className="tl-states-pop" role="dialog" aria-label="States using this topic">
          <div className="tl-states-box">
            <div className="tl-states-head">
              <strong>{showStates.title}</strong>
              <button type="button" className="lb-btn" onClick={() => setShowStates(null)}>
                Close
              </button>
            </div>
            {showStates.states.length ? (
              <>
                <p className="lb-hint">
                  On {showStates.states.length} state
                  {showStates.states.length === 1 ? "" : "s"}
                  {usageAt && <> · counted {new Date(usageAt).toLocaleDateString()}</>}
                </p>
                <ul className="tl-states-list">
                  {showStates.states.map((s2) => (
                    <li key={s2.state}>
                      <strong>{s2.stateName}</strong>{" "}
                      <span className="tl-states-grades">
                        {(s2.grades || []).join(", ")} · {(s2.subjects || []).join(", ")}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="lb-hint">
                Not on any state page yet. Attach it from Suggested topics, or run{" "}
                <code>node src/scripts/audit-topic-usage.mjs --write</code> if it has
                been attached since the last count.
              </p>
            )}
          </div>
        </div>
      )}

      </>)}

      {toast && <div className={`tl-toast tl-toast-${toast.tone}`}>{toast.message}</div>}

      {/* Every rule is scoped under `.lb-page .tl-panel`. The builder's own
          .lb-btn styles are inherited rather than restated, so these buttons
          stay the same blue as the rest of the page if that palette changes. */}
      <style>{`
        .lb-page .tl-panel { border-color: #d9d4ff; }
        .lb-page .tl-panel .tl-step { background: var(--lb-violet); color: #fff; }
        .lb-page .tl-panel .tl-head-actions { display: flex; gap: 8px; flex: none; }

        .lb-page .tl-panel .tl-controls { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; margin-bottom: 14px; }
        .lb-page .tl-panel .tl-tabs { display: flex; gap: 6px; flex-wrap: wrap; }
        .lb-page .tl-panel .tl-tab { display: inline-flex; align-items: center; gap: 7px; border: 1.5px solid var(--lb-line); background: #fff; color: var(--lb-ink-soft); border-radius: 999px; padding: 7px 14px; font-size: 13px; font-weight: 700; font-family: inherit; cursor: pointer; }
        .lb-page .tl-panel .tl-tab:hover { background: var(--lb-tint); color: var(--lb-blue-dark); }
        .lb-page .tl-panel .tl-tab.is-on { background: var(--lb-blue); border-color: var(--lb-blue); color: #fff; }
        .lb-page .tl-panel .tl-tab-count { font-weight: 600; opacity: .7; font-size: 12px; }
        .lb-page .tl-panel .tl-tab-flag { background: var(--lb-red); color: #fff; border-radius: 999px; padding: 1px 7px; font-size: 11px; }
        .lb-page .tl-panel .tl-tab.is-on .tl-tab-flag { background: rgba(255,255,255,.28); }

        .lb-page .tl-panel .tl-filter { flex: 1 1 200px; min-width: 160px; padding: 9px 13px; border: 1.5px solid var(--lb-line); border-radius: 11px; font-family: inherit; font-size: 13px; color: var(--lb-ink); }
        .lb-page .tl-panel .tl-filter:focus { outline: none; border-color: var(--lb-blue); }

        /* Status filter. The selected state is painted in the same two colours
           as the rows it produces, so pressing Live and getting a table of blue
           rows is one idea rather than two. Deliberately a segmented control
           rather than more pills — it is a different axis from the subject
           tabs, and looking identical to them would invite reading it as a
           fourth subject. */
        .lb-page .tl-panel .tl-seg { display: inline-flex; flex: none; border: 1.5px solid var(--lb-line); border-radius: 999px; overflow: hidden; background: #fff; }
        .lb-page .tl-panel .tl-seg-btn { display: inline-flex; align-items: center; gap: 6px; border: 0; background: none; font-family: inherit; font-size: 12.5px; font-weight: 700; color: var(--lb-ink-soft); padding: 7px 15px; cursor: pointer; }
        .lb-page .tl-panel .tl-seg-btn + .tl-seg-btn { border-left: 1.5px solid var(--lb-line); }
        .lb-page .tl-panel .tl-seg-btn:hover { background: var(--lb-tint); }
        .lb-page .tl-panel .tl-seg-btn.is-on.tl-seg-all { background: var(--lb-blue); color: #fff; }
        .lb-page .tl-panel .tl-seg-btn.is-on.tl-seg-live { background: #b9d8f7; color: #10375c; }
        .lb-page .tl-panel .tl-seg-btn.is-on.tl-seg-draft { background: #ffcdb0; color: #6d3410; }
        .lb-page .tl-panel .tl-seg-n { font-weight: 600; opacity: .75; font-size: 12px; font-variant-numeric: tabular-nums; }

        .lb-page .tl-panel .tl-table-wrap { border: 1px solid var(--lb-line); border-radius: 14px; overflow: auto; max-height: 520px; }
        .lb-page .tl-panel .tl-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .lb-page .tl-panel .tl-table th { position: sticky; top: 0; z-index: 1; background: var(--lb-tint); color: var(--lb-ink-soft); text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: .05em; padding: 10px 12px; border-bottom: 1px solid var(--lb-line); white-space: nowrap; }
        .lb-page .tl-panel .tl-table td { padding: 9px 12px; border-bottom: 1px solid #eef2fb; vertical-align: middle; }
        .lb-page .tl-panel .tl-table tbody tr { cursor: pointer; }
        .lb-page .tl-panel .tl-table tbody tr:hover { background: var(--lb-tint); }

        /* Status owns the row background: blue is live, warm is draft. Every
           row carries one of the two, so what is and is not reaching a
           classroom reads off the shape of the table without anyone having to
           scroll right to the Status column. Each needs its own :hover — the
           generic tr:hover above has the same specificity as these, so without
           it the tint would swallow the hover feedback. */
        .lb-page .tl-panel .tl-table tbody tr.is-live { background:rgb(178, 200, 227); }
        .lb-page .tl-panel .tl-table tbody tr.is-live:hover { background: #d5e8fd; }
        .lb-page .tl-panel .tl-table tbody tr.is-draft { background:rgb(248, 191, 160); }
        .lb-page .tl-panel .tl-table tbody tr.is-draft:hover { background: #ffe2d2; }

        /* "Needs attention" used to own the row background too, which would
           now cancel the status tint on exactly the rows most worth reading.
           It marks the row with a left bar instead, so both signals show at
           once, and the offending cells are already red inside the row. */
        .lb-page .tl-panel .tl-table tbody tr.is-flagged { box-shadow: inset 4px 0 0 #ef8b2c; }

        /* Declared last so it beats both, and pushed to a stronger indigo so
           the open row reads as a selection rather than as a slightly
           different shade of the live blue sitting all around it. */
        .lb-page .tl-panel .tl-table tbody tr.is-open,
        .lb-page .tl-panel .tl-table tbody tr.is-open:hover { background: #dfe5ff; box-shadow: inset 4px 0 0 var(--lb-blue); }
        .lb-page .tl-panel .tl-td-title { font-weight: 700; color: var(--lb-ink); }
        .lb-page .tl-panel .tl-slug { background: var(--lb-tint); color: var(--lb-blue-dark); padding: 2px 7px; border-radius: 6px; font-size: 11.5px; }
        .lb-page .tl-panel .tl-type { color: var(--lb-ink-soft); font-size: 12px; }

        .lb-page .tl-panel .tl-cell { font-size: 12px; max-width: 190px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: inline-block; vertical-align: bottom; }
        .lb-page .tl-panel .tl-cell-ok { color: var(--lb-green); font-weight: 600; }
        .lb-page .tl-panel .tl-cell-bad { color: var(--lb-red); font-weight: 700; text-decoration: underline wavy var(--lb-red); }
        .lb-page .tl-panel .tl-cell-warn { color: #b7791f; font-weight: 600; }
        .lb-page .tl-panel .tl-cell-empty { color: #b9c4dc; }

        .lb-page .tl-panel .tl-pill { font-size: 11px; font-weight: 700; border-radius: 999px; padding: 2px 9px; background: var(--lb-tint); color: var(--lb-ink-soft); white-space: nowrap; }
        .lb-page .tl-panel .tl-pill-ok { background: #e6f9f2; color: #0b7a5c; }
        .lb-page .tl-panel .tl-pill-bad { background: #fff1f1; color: var(--lb-red); }
        .lb-page .tl-panel .tl-pill-warn { background: #fff6e5; color: #8a6416; }
        /* The status pill used to borrow tl-pill-ok, which is also the media
           card's "set" pill — recolouring it to match the new blue row would
           have repainted that one too. Its own class instead. */
        .lb-page .tl-panel .tl-pill-live { background: #d3e6fb; color: #1b4d86; }
        .lb-page .tl-panel .tl-pill-draft { background: #ffdcc6; color: #92400e; }

        .lb-page .tl-panel .tl-empty { color: var(--lb-ink-soft); font-size: 13px; line-height: 1.7; }
        .lb-page .tl-panel .tl-empty code, .lb-page .tl-panel .tl-tiny code { background: var(--lb-tint); padding: 2px 7px; border-radius: 6px; font-size: 11.5px; color: var(--lb-blue-dark); }
        .lb-page .tl-panel .tl-tiny { font-size: 11.5px; color: var(--lb-ink-soft); line-height: 1.55; margin: 6px 0 0; font-style: normal; }

        .lb-page .tl-panel .tl-drawer { margin-top: 18px; border-top: 2px solid var(--lb-line); padding-top: 16px; }
        .lb-page .tl-panel .tl-drawer-head { display: flex; justify-content: space-between; align-items: center; gap: 12px; }
        .lb-page .tl-panel .tl-drawer-head h3 { margin: 0; font-size: 17px; color: var(--lb-ink); }
        .lb-page .tl-panel .tl-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 12px; margin: 14px 0; }
        .lb-page .tl-panel .tl-grid label, .lb-page .tl-panel .tl-block { display: flex; flex-direction: column; gap: 5px; }
        .lb-page .tl-panel .tl-block { margin-bottom: 14px; }
        .lb-page .tl-panel .tl-grid > label > span, .lb-page .tl-panel .tl-block > span { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: var(--lb-ink-soft); }
        .lb-page .tl-panel .tl-grid input, .lb-page .tl-panel .tl-grid select, .lb-page .tl-panel .tl-block textarea { padding: 9px 11px; border: 1.5px solid var(--lb-line); border-radius: 11px; font-family: inherit; font-size: 13px; color: var(--lb-ink); background: #fff; }
        .lb-page .tl-panel .tl-grid input:focus, .lb-page .tl-panel .tl-grid select:focus, .lb-page .tl-panel .tl-block textarea:focus { outline: none; border-color: var(--lb-blue); }
        .lb-page .tl-panel .tl-grid input:disabled { background: var(--lb-tint); color: var(--lb-ink-soft); }

        .lb-page .tl-panel .tl-gen-games { margin: -4px 0 14px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .lb-page .tl-panel .tl-lyrics { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12.5px; line-height: 1.7; resize: vertical; }
        .lb-page .tl-panel .tl-count { font-style: normal; font-weight: 600; color: var(--lb-blue-dark); text-transform: none; letter-spacing: 0; }
        .lb-page .tl-panel .tl-media-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 12px; }
        .lb-page .tl-panel .tl-media { border: 1.5px solid var(--lb-line); border-radius: 14px; padding: 13px; }
        .lb-page .tl-panel .tl-media.is-flagged { border-color: #ffd9a8; background: #fffdf7; }
        .lb-page .tl-panel .tl-media-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 9px; }
        .lb-page .tl-panel .tl-media-label { font-weight: 800; color: var(--lb-ink); font-size: 13px; }
        .lb-page .tl-panel .tl-media-preview { margin-bottom: 9px; display: flex; flex-direction: column; gap: 6px; align-items: flex-start; }
        .lb-page .tl-panel .tl-media-preview img, .lb-page .tl-panel .tl-media-preview video { max-width: 100%; max-height: 130px; border-radius: 9px; display: block; }
        .lb-page .tl-panel .tl-media-preview audio { width: 100%; }
        .lb-page .tl-panel .tl-file { font-size: 11px; color: var(--lb-ink-soft); background: var(--lb-tint); padding: 2px 7px; border-radius: 6px; word-break: break-all; }
        .lb-page .tl-panel .tl-options { list-style: none; margin: 0 0 9px; padding: 0; display: flex; flex-direction: column; gap: 5px; }
        .lb-page .tl-panel .tl-options li { display: flex; justify-content: space-between; gap: 8px; align-items: center; }
        .lb-page .tl-panel .tl-options button { background: none; border: 0; color: var(--lb-blue); font-weight: 700; cursor: pointer; text-align: left; padding: 0; font-size: 12px; font-family: inherit; }
        .lb-page .tl-panel .tl-options button:hover { text-decoration: underline; }
        .lb-page .tl-panel .tl-options li.is-bad button { color: var(--lb-red); }
        .lb-page .tl-panel .tl-options span { color: var(--lb-ink-soft); font-size: 11px; flex: none; }
        .lb-page .tl-panel .tl-media-actions { display: flex; flex-direction: column; gap: 9px; align-items: flex-start; }
        /* The upload control is a <label> styled as .lb-btn. Its input is
           visually hidden but NOT display:none, so it stays a real, clickable
           form control that the label can open. */
        .lb-page .tl-panel .tl-upload { position: relative; cursor: pointer; }
        .lb-page .tl-panel .tl-upload.is-disabled { opacity: .55; cursor: not-allowed; pointer-events: none; }
        .lb-page .tl-panel .tl-upload:focus-within { outline: 2px solid var(--lb-blue); outline-offset: 2px; }
        .lb-page .tl-panel .tl-file-input { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; opacity: 0; }
        .lb-page .tl-panel .tl-play-error { margin: 0; font-size: 12px; line-height: 1.5; color: #8a1c1c; background: #fff1f1; border: 1px solid #f5c2c2; border-radius: 9px; padding: 7px 10px; }
        .lb-page .tl-panel .tl-play-error a { color: inherit; font-weight: 700; }
        .lb-page .tl-panel .tl-confirm { background: #fff6e5; border: 1.5px solid #ffd9a8; border-radius: 11px; padding: 11px; }
        .lb-page .tl-panel .tl-confirm p { margin: 0 0 9px; font-size: 12px; color: #7a5a12; line-height: 1.55; }
        .lb-page .tl-panel .tl-confirm-actions { display: flex; gap: 7px; }
        .lb-page .tl-panel .tl-drawer-actions { display: flex; gap: 9px; align-items: center; flex-wrap: wrap; margin-top: 16px; }

        /* Who-sees-this notice at the top of the drawer. Warm when a save
           would reach nobody (draft, or a slug no page carries), blue when it
           reaches real pages — the same two colours as the table rows. */
        .lb-page .tl-panel .tl-reach { margin: 14px 0 4px; border-radius: 12px; padding: 12px 14px; border: 1.5px solid; font-size: 13px; line-height: 1.55; }
        .lb-page .tl-panel .tl-reach p { margin: 0; }
        .lb-page .tl-panel .tl-reach p + p, .lb-page .tl-panel .tl-reach p + .tl-twins { margin-top: 8px; }
        .lb-page .tl-panel .tl-reach code { background: rgba(255,255,255,.7); padding: 1px 6px; border-radius: 6px; font-size: 12px; }
        .lb-page .tl-panel .tl-reach-ok { background: #eaf3fd; border-color: #b9d8f7; color: #10375c; }
        .lb-page .tl-panel .tl-reach-warn { background: #fff4ec; border-color: #ffcdb0; color: #6d3410; }
        .lb-page .tl-panel .tl-same-slug { margin: 0 0 8px; padding: 8px 10px; border-radius: 10px; background: #fff1f1; border: 1px solid #ffc4c4; color: #8b1a1a; font-size: 13px; line-height: 1.45; }
        .lb-page .tl-panel .tl-twins { list-style: none; margin: 8px 0 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
        .lb-page .tl-panel .tl-twins li { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; background: #fff; border: 1px solid var(--lb-line); border-radius: 10px; padding: 7px 10px; }
        .lb-page .tl-panel .tl-twins-actions { display: flex; gap: 6px; margin-left: auto; flex-wrap: wrap; }

        .lb-page .tl-panel .tl-states { border: 0; border-radius: 999px; padding: 2px 10px; font: inherit; font-size: 12px; font-weight: 700; cursor: pointer; }
        .lb-page .tl-panel .tl-states-on { background: #dbeafe; color: #1e40af; }
        .lb-page .tl-panel .tl-states-off { background: #f1f5f9; color: #94a3b8; }
        .lb-page .tl-panel .tl-states:hover { outline: 1px solid var(--lb-blue); }
        .lb-page .tl-panel .tl-states-pop { position: fixed; inset: 0; background: rgba(15,23,42,.45); display: flex; align-items: center; justify-content: center; z-index: 9998; padding: 20px; }
        .lb-page .tl-panel .tl-states-box { background: var(--lb-surface); border-radius: 12px; padding: 18px 20px; max-width: 460px; width: 100%; max-height: 70vh; overflow: auto; box-shadow: 0 12px 40px rgba(15,23,42,.28); }
        .lb-page .tl-panel .tl-states-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 6px; }
        .lb-page .tl-panel .tl-states-list { list-style: none; margin: 8px 0 0; padding: 0; font-size: 13px; }
        .lb-page .tl-panel .tl-states-list li { padding: 5px 0; border-bottom: 1px solid var(--lb-line); }
        .lb-page .tl-panel .tl-states-grades { color: var(--lb-ink-soft); font-size: 11px; }

        .lb-page .tl-panel .tl-attach { border-top: 1px solid var(--lb-line); margin-top: 16px; padding-top: 14px; }
        .lb-page .tl-panel .tl-attach-head { display: flex; flex-direction: column; gap: 2px; margin-bottom: 8px; }
        .lb-page .tl-panel .tl-attach-row { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; margin-bottom: 8px; }
        .lb-page .tl-panel .tl-attach-row input { flex: 1 1 220px; min-width: 160px; }
        .lb-page .tl-panel .tl-attach-error { color: var(--lb-red); }
        .lb-page .tl-panel .tl-attach-done { color: #166534; background: #dcfce7; padding: 6px 10px; border-radius: 4px; }
        .lb-page .tl-panel .tl-attach-results { list-style: none; margin: 0; padding: 0; max-height: 240px; overflow: auto; border: 1px solid var(--lb-line); border-radius: 6px; }
        .lb-page .tl-panel .tl-attach-results li { border-bottom: 1px solid var(--lb-line); }
        .lb-page .tl-panel .tl-attach-results button { display: block; width: 100%; text-align: left; background: none; border: 0; padding: 7px 10px; font: inherit; font-size: 12px; cursor: pointer; }
        .lb-page .tl-panel .tl-attach-results button:hover { background: var(--lb-tint); }
        .lb-page .tl-panel .tl-attach-desc { display: block; color: var(--lb-ink-soft); font-size: 11px; margin-top: 2px; }
        .lb-page .tl-panel .tl-attach-confirm { border: 2px solid var(--lb-amber); border-radius: 6px; padding: 12px 14px; background: var(--lb-tint); }
        .lb-page .tl-panel .tl-attach-picks { display: flex; flex-wrap: wrap; gap: 4px 14px; max-height: 140px; overflow: auto; margin: 8px 0; font-size: 12px; }
        .lb-page .tl-panel .tl-attach-picks label { display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; cursor: pointer; }

        .lb-page .tl-panel .tl-toast { position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); padding: 12px 22px; border-radius: 12px; font-size: 13px; font-weight: 700; z-index: 9999; box-shadow: 0 8px 28px rgba(22,35,63,.22); }
        .lb-page .tl-panel .tl-toast-ok { background: var(--lb-blue); color: #fff; }
        .lb-page .tl-panel .tl-toast-bad { background: var(--lb-red); color: #fff; }

        /* ---------------- EDITOR DRAWER: sections and fields ----------------
           The editor used to be one long run of identical grey labels and
           identical white boxes, so the eye slid straight past fields. Now:
             * the drawer sits on a soft tinted panel,
             * each group of fields is a white card with a numbered heading
               and its own coloured edge, so you always know which part you
               are in,
             * labels are dark and sentence-case (not tiny grey capitals),
             * every input is a clearly filled box with a visible border and
               a blue focus ring.
           Same --lb-* palette as the rest of the builder. */
        .lb-page .tl-panel .tl-drawer { background: #f3f6fd; border: 1.5px solid var(--lb-line); border-top: 4px solid var(--lb-violet); border-radius: 16px; padding: 18px 18px 0; }
        .lb-page .tl-panel .tl-drawer-head { flex-wrap: wrap; }
        .lb-page .tl-panel .tl-drawer-title { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; min-width: 0; flex: 1 1 220px; }
        .lb-page .tl-panel .tl-drawer-title h3 { font-size: 19px; }

        .lb-page .tl-panel .tl-status-toggle { display: inline-flex; align-items: center; gap: 10px; padding: 5px 6px 5px 12px; border-radius: 999px; border: 1.5px solid; background: #fff; }
        .lb-page .tl-panel .tl-status-toggle.is-draft { border-color: #ffcdb0; background: #fff4ec; }
        .lb-page .tl-panel .tl-status-toggle.is-live { border-color: #b9d8f7; background: #eaf3fd; }
        .lb-page .tl-panel .tl-status-now { display: inline-flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 800; letter-spacing: .02em; }
        .lb-page .tl-panel .tl-status-toggle.is-draft .tl-status-now { color: #92400e; }
        .lb-page .tl-panel .tl-status-toggle.is-live .tl-status-now { color: #1b4d86; }
        .lb-page .tl-panel .tl-status-dot { width: 10px; height: 10px; border-radius: 50%; background: currentColor; box-shadow: 0 0 0 3px rgba(255,255,255,.9); }
        .lb-page .tl-panel .tl-status-toggle.is-live .tl-status-dot { background: var(--lb-green); }
        .lb-page .tl-panel .tl-status-toggle.is-draft .tl-status-dot { background: #f08a3c; }
        .lb-page .tl-panel .tl-status-btn { border-radius: 999px; min-width: 112px; }
        .lb-page .tl-panel .tl-status-note { margin: 8px 0 0; }

        .lb-page .tl-panel .tl-section { background: #fff; border: 1.5px solid var(--lb-line); border-left: 5px solid var(--sec, var(--lb-blue)); border-radius: 14px; padding: 14px 16px 16px; margin: 14px 0; box-shadow: 0 1px 2px rgba(22,35,63,.04); }
        .lb-page .tl-panel .tl-sec-basics { --sec: var(--lb-blue); }
        .lb-page .tl-panel .tl-sec-desc { --sec: #0ea5a4; }
        .lb-page .tl-panel .tl-sec-lyrics { --sec: var(--lb-violet); }
        .lb-page .tl-panel .tl-sec-media { --sec: #f08a3c; }
        .lb-page .tl-panel .tl-sec-attach { --sec: #94a3b8; }
        .lb-page .tl-panel .tl-section-head { display: flex; align-items: flex-start; gap: 11px; padding-bottom: 11px; margin-bottom: 12px; border-bottom: 1px solid #edf1fa; }
        .lb-page .tl-panel .tl-section-head h4 { margin: 0; font-size: 15px; font-weight: 800; color: var(--lb-ink); }
        .lb-page .tl-panel .tl-section-head p { margin: 2px 0 0; font-size: 12.5px; color: var(--lb-ink-soft); line-height: 1.45; }
        .lb-page .tl-panel .tl-section-num { flex: none; width: 26px; height: 26px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; background: var(--sec, var(--lb-blue)); color: #fff; font-size: 13px; font-weight: 800; margin-top: 1px; }
        .lb-page .tl-panel .tl-section .tl-grid { margin: 0; gap: 14px 16px; }
        .lb-page .tl-panel .tl-section .tl-block { margin-bottom: 0; }
        .lb-page .tl-panel .tl-section .tl-gen-games { margin: 10px 0 0; }
        /* The old border-top divider is replaced by the card itself. */
        .lb-page .tl-panel .tl-section.tl-attach { border-top-width: 1.5px; margin-top: 14px; padding-top: 14px; }

        /* Labels: dark, readable, sentence case. */
        .lb-page .tl-panel .tl-grid > label > span,
        .lb-page .tl-panel .tl-block > span { font-size: 13px; font-weight: 700; text-transform: none; letter-spacing: 0; color: var(--lb-ink); }
        .lb-page .tl-panel .tl-req { font-size: 10.5px; font-weight: 700; color: var(--lb-red); background: #fff1f1; border-radius: 999px; padding: 1px 7px; margin-left: 6px; vertical-align: 1px; }

        /* Inputs: filled, bordered, obvious. */
        .lb-page .tl-panel .tl-section input:not([type="checkbox"]):not([type="file"]),
        .lb-page .tl-panel .tl-section select,
        .lb-page .tl-panel .tl-section textarea { padding: 10px 12px; min-height: 42px; border: 1.5px solid #cfd8ec; border-radius: 10px; background: #f8faff; font-family: inherit; font-size: 14px; color: var(--lb-ink); box-sizing: border-box; transition: border-color .15s, box-shadow .15s, background .15s; }
        .lb-page .tl-panel .tl-section textarea { min-height: 0; }
        .lb-page .tl-panel .tl-section input:not([type="checkbox"]):not([type="file"]):hover,
        .lb-page .tl-panel .tl-section select:hover,
        .lb-page .tl-panel .tl-section textarea:hover { border-color: #aebbdc; }
        .lb-page .tl-panel .tl-section input:not([type="checkbox"]):not([type="file"]):focus,
        .lb-page .tl-panel .tl-section select:focus,
        .lb-page .tl-panel .tl-section textarea:focus { outline: none; background: #fff; border-color: var(--lb-blue); box-shadow: 0 0 0 3px rgba(42,77,255,.16); }
        .lb-page .tl-panel .tl-section input:disabled { background: #eef1f8; color: var(--lb-ink-soft); border-style: dashed; }
        .lb-page .tl-panel .tl-section .tl-lyrics { font-size: 13px; background: #fbfaff; }

        /* Media cards inside the Media section: tinted so each file box
           stands apart from the white section card. */
        .lb-page .tl-panel .tl-sec-media .tl-media { background: #fffaf5; border-color: #f6dcc4; }
        .lb-page .tl-panel .tl-sec-media .tl-media.is-flagged { background: #fff6e8; border-color: #ffc98a; }

        /* Save bar stays on screen while you scroll the editor. */
        .lb-page .tl-panel .tl-drawer-actions { position: sticky; bottom: 0; z-index: 3; margin: 16px -18px 0; padding: 12px 18px; background: rgba(255,255,255,.96); border-top: 1.5px solid var(--lb-line); border-radius: 0 0 14px 14px; backdrop-filter: blur(4px); }

        @media (max-width: 760px) {
          .lb-page .tl-panel .tl-table th:nth-child(5),
          .lb-page .tl-panel .tl-table td:nth-child(5),
          .lb-page .tl-panel .tl-table th:nth-child(6),
          .lb-page .tl-panel .tl-table td:nth-child(6),
          .lb-page .tl-panel .tl-table th:nth-child(8),
          .lb-page .tl-panel .tl-table td:nth-child(8) { display: none; }
        }
      `}</style>
    </section>
  );
};

export default TopicLibraryPanel;
